import { useMemo } from "react";

let idCounter = 0;

/**
 * Characters that are legal in an HTML `id` but not in an unescaped CSS identifier.
 *
 * Our labels are namespaced with a dot (`"DDSSearchField.input"`), which produced ids like
 * `DDSSearchField.input19`. Dropped into an attribute selector without quoting —
 * `label[for=DDSSearchField.input19]` — the dot starts a class selector and `querySelectorAll`
 * throws `SyntaxError: ... is not a valid selector`, taking down whoever ran the query. We don't
 * control every caller that builds such a selector (accessibility tooling, analytics and
 * extensions all walk the DOM this way), so keep the ids selector-safe at the source instead.
 * The dot only ever separated the component name from the part name, so a hyphen reads the same.
 */
const CSS_UNSAFE_ID_CHARS = /[^A-Za-z0-9_-]/g;

const toSelectorSafe = (label: string) => label.replace(CSS_UNSAFE_ID_CHARS, "-");

const uniqueId = (label: string) => `${toSelectorSafe(label)}${++idCounter}`;

type Identifiers<Labels extends string[]> = {
  [K in keyof Labels]: string;
};

export const useUniqueIds = <const Labels extends string[]>(labels: Labels): Identifiers<Labels> =>
  // eslint-disable-next-line react-hooks/exhaustive-deps -- this looks to me like it could be buggy either way...
  useMemo(() => labels.map(uniqueId) as Identifiers<Labels>, []);

export const useUniqueId = (label: string): string => useMemo(() => uniqueId(label), [label]);
